import { isValid, startOfDay } from "date-fns";

import {
  dateTimeStringToDateTime,
  formatHeaderDateString,
} from "lib/timeFormats";

export interface SaleOption {
  value: number;
  label: string;
  title: string;
  detail: string;
}

interface BuildMoveScansToSaleOptionsArgs {
  consignableSales: Record<string | number, ConsignableSale>;
  currentLivestockSaleId: number | null;
  ownSales: Record<string | number, Sale>;
  speciesById: Record<number, Species>;
  today?: Date;
}

interface NormalizedSale {
  date: string;
  deploymentSales: DeploymentSale[];
  livestockSaleId: number;
  saleTitle: string | null;
  saleType: string | null;
  saleyardName: string | null;
  speciesId: number;
}

function getSaleOptionLabel(
  sale: NormalizedSale,
  deploymentSale: DeploymentSale,
  speciesName: string,
): Pick<SaleOption, "label" | "title" | "detail"> {
  const date = sale.date
    ? formatHeaderDateString(dateTimeStringToDateTime(sale.date))
    : "";
  const saleName = sale.saleTitle || sale.saleType || "Sale";
  const agencyName =
    deploymentSale.livestock_agency_name ||
    deploymentSale.display_name ||
    "Agency";
  const detail = [date, speciesName, sale.saleyardName]
    .filter(Boolean)
    .join(" - ");
  const title = `${saleName} - ${agencyName}`;

  return {
    label: [title, detail].filter(Boolean).join(" - "),
    title,
    detail,
  };
}

function normalizeConsignableSale(sale: ConsignableSale): NormalizedSale {
  return {
    date: sale.date,
    deploymentSales: sale.deploymentSales || [],
    livestockSaleId: sale.livestockSaleId,
    saleTitle: sale.saleTitle,
    saleType: sale.saleType,
    saleyardName: sale.saleyardName,
    speciesId: sale.speciesId,
  };
}

function normalizeOwnSale(sale: Sale): NormalizedSale {
  return {
    date: sale.date,
    deploymentSales: sale.deployment_sales || [],
    livestockSaleId: sale.livestocksale_id,
    saleTitle: sale.sale_title,
    saleType: sale.sale_type,
    saleyardName: sale.saleyard_name,
    speciesId: sale.species_id,
  };
}

function isCandidateSale(
  sale: NormalizedSale,
  currentLivestockSaleId: number | null,
  today: Date,
): boolean {
  const saleDate = dateTimeStringToDateTime(sale.date);

  return (
    isValid(saleDate) &&
    startOfDay(saleDate) >= startOfDay(today) &&
    sale.livestockSaleId !== currentLivestockSaleId
  );
}

export function buildMoveScansToSaleOptions({
  consignableSales,
  currentLivestockSaleId,
  ownSales,
  speciesById,
  today = new Date(),
}: BuildMoveScansToSaleOptionsArgs): SaleOption[] {
  const saleOptionsByDeploymentSaleId = [
    ...Object.values(consignableSales).map(normalizeConsignableSale),
    ...Object.values(ownSales).map(normalizeOwnSale),
  ].reduce<Record<number, SaleOption>>((acc, sale) => {
    if (!isCandidateSale(sale, currentLivestockSaleId, today)) {
      return acc;
    }

    sale.deploymentSales.forEach(deploymentSale => {
      const deploymentSaleId = deploymentSale.deployment_sale_id;
      if (!deploymentSaleId || acc[deploymentSaleId]) {
        return;
      }
      acc[deploymentSaleId] = {
        value: deploymentSaleId,
        ...getSaleOptionLabel(
          sale,
          deploymentSale,
          speciesById[sale.speciesId]?.name || "",
        ),
      };
    });
    return acc;
  }, {});

  return Object.values(saleOptionsByDeploymentSaleId).sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}
